/**
 * Utility per gestire le variabili dinamiche nei modelli fattura.
 * Supporta highlight {{var}} in textarea e autocomplete.
 */

// Regex per trovare tutte le variabili nel testo
const VAR_REGEX = /\{\{([^}]+)\}\}/g;

/**
 * Restituisce un array di tutte le variabili trovate in un testo.
 * Es: "Retta {{periodo.mese_anno}}" → ["periodo.mese_anno"]
 */
export function extractVariabili(testo) {
  if (!testo) return [];
  const found = [];
  let match;
  while ((match = VAR_REGEX.exec(testo)) !== null) {
    found.push(match[1].trim());
  }
  return found;
}

/**
 * Evidenzia le variabili {{...}} nel testo come HTML span.
 * Da usare con dangerouslySetInnerHTML in un overlay sopra la textarea.
 */
export function highlightVariabili(testo) {
  if (!testo) return "";
  return testo.replace(VAR_REGEX, (_, chiave) => {
    return `<span class="variable-highlight">{{${chiave.trim()}}}</span>`;
  });
}

// Mesi in italiano (allineato al backend)
const MESI_NOME_IT = {
  "01": "Gennaio", "02": "Febbraio", "03": "Marzo", "04": "Aprile",
  "05": "Maggio", "06": "Giugno", "07": "Luglio", "08": "Agosto",
  "09": "Settembre", "10": "Ottobre", "11": "Novembre", "12": "Dicembre",
};

/**
 * Costruisce il contesto variabili da fascicolo + mese + struttura
 * (allineato a modelli_fattura_resolver.py lato backend).
 *
 * Supporta sia il fascicolo completo (anagrafica.*) sia la lista piatta
 * restituita da GET /fascicoli-assistenziali (nome, cognome, retta_mensile in cima).
 */
export function buildContestoFattura(fascicolo, mese, struttura) {
  if (!fascicolo && !mese) return {};

  const anagrafica = fascicolo?.anagrafica || {};
  const amm = fascicolo?.amministrativa || {};

  let nome = (anagrafica.nome || fascicolo?.nome || "").trim();
  let cognome = (anagrafica.cognome || fascicolo?.cognome || "").trim();

  let nomeCompleto = (
    anagrafica.nome_completo ||
    fascicolo?.nome_completo ||
    `${nome} ${cognome}`.trim() ||
    `${cognome} ${nome}`.trim()
  );

  // Se abbiamo solo nome_completo, prova a splittare (es. "Francesca Neri")
  if (nomeCompleto && !nome && !cognome) {
    const parts = nomeCompleto.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      nome = parts[0];
      cognome = parts.slice(1).join(" ");
    } else if (parts.length === 1) {
      nome = parts[0];
    }
  }

  // Se manca nome_completo ma abbiamo nome/cognome
  if (!nomeCompleto && (nome || cognome)) {
    nomeCompleto = `${nome} ${cognome}`.trim();
  }

  let annoS = "";
  let meseNumS = "";
  if (mese && mese.includes("-")) {
    [annoS, meseNumS] = mese.split("-");
  }

  let retta = 0;
  try {
    const rawRetta = amm.retta_mensile ?? fascicolo?.retta_mensile ?? 0;
    retta = parseFloat(rawRetta) || 0;
  } catch {
    retta = 0;
  }

  const meseNome = MESI_NOME_IT[meseNumS] || meseNumS;

  const codiceFiscale = (
    anagrafica.codice_fiscale ||
    fascicolo?.codice_fiscale ||
    ""
  ).trim();

  const sesso = (anagrafica.sesso || fascicolo?.sesso || "").toString().trim().toUpperCase();
  const titolo = ["F", "FEMMINA", "FEMALE", "DONNA"].includes(sesso) ? "Sig.ra" : "Sig.";
  const riferimento = nomeCompleto ? `${titolo} ${nomeCompleto}`.trim() : titolo;

  return {
    "ospite.nome": nome,
    "ospite.cognome": cognome,
    "ospite.nome_completo": nomeCompleto,
    "ospite.codice_fiscale": codiceFiscale,
    "ospite.titolo": titolo,
    "ospite.riferimento": riferimento,
    "periodo.mese": meseNumS,
    "periodo.anno": annoS,
    "periodo.mese_nome": meseNome,
    "periodo.mese_anno": annoS && meseNome ? `${meseNome} ${annoS}` : "",
    "retta.importo": retta.toFixed(2),
    "struttura.nome": (struttura?.name || struttura?.nome || "").trim(),
  };
}

/**
 * Sostituisce le variabili in un testo con i valori del contesto.
 * (Usato per anteprima lato client senza chiamata API.)
 */
export function risolviVariabili(testo, contesto = {}) {
  if (!testo) return testo;
  return testo.replace(VAR_REGEX, (_, chiave) => {
    const k = chiave.trim();
    return k in contesto ? String(contesto[k]) : `{{${k}}}`;
  });
}

/**
 * Suggerimenti per autocomplete.
 * Dato il testo corrente e la posizione del cursore, restituisce le variabili
 * disponibili filtrate in base a cosa ha digitato l'utente dopo "{{".
 */
export function getSuggerimenti(testo, cursorPos, variabiliDisponibili = []) {
  if (!testo || cursorPos === undefined) return [];
  const before = testo.slice(0, cursorPos);
  const lastOpen = before.lastIndexOf("{{");
  if (lastOpen === -1) return [];
  const typed = before.slice(lastOpen + 2);
  if (typed.includes("}}")) return [];
  return variabiliDisponibili.filter((v) =>
    v.chiave.toLowerCase().includes(typed.toLowerCase())
  );
}

/**
 * Sostituisce il testo parziale dopo {{ con la variabile selezionata dall'autocomplete.
 * Restituisce il nuovo testo e la nuova posizione del cursore.
 */
export function applicaSuggerimento(testo, cursorPos, chiave) {
  const before = testo.slice(0, cursorPos);
  const after = testo.slice(cursorPos);
  const lastOpen = before.lastIndexOf("{{");
  if (lastOpen === -1) return { testo, cursorPos };
  const newBefore = before.slice(0, lastOpen) + `{{${chiave}}}`;
  const newTesto = newBefore + after;
  return { testo: newTesto, cursorPos: newBefore.length };
}

// Etichette tipi modello
export const TIPO_MODELLO_LABELS = {
  retta: "Retta",
  servizi: "Servizi",
  acconto: "Acconto",
  saldo: "Saldo",
  dimissione: "Dimissione",
  altro: "Altro",
};

// Etichette fonti riga
export const FONTE_RIGA_LABELS = {
  manuale: "Manuale",
  retta: "Retta mensile",
  prima_nota_spese: "Prima Nota — Spese",
  prima_nota_consumi: "Prima Nota — Consumi",
  deblistering: "Deblistering",
  deposito_cauzionale: "Deposito cauzionale",
};

// Badge colori per tipo modello
export const TIPO_MODELLO_COLORS = {
  retta: "bg-blue-100 text-blue-700",
  servizi: "bg-violet-100 text-violet-700",
  acconto: "bg-amber-100 text-amber-700",
  saldo: "bg-orange-100 text-orange-700",
  dimissione: "bg-rose-100 text-rose-700",
  altro: "bg-slate-100 text-slate-600",
};
